import React, {createContext, useContext, useEffect, useMemo, useRef, useState, useCallback} from 'react';
import {useLocation} from 'react-router-dom';
import {AppCtx} from '../../../AppCtx';
import yjsDocumentService from '../../../infrastructure/sync/YjsDocumentService';
import {getWorkspaceIdForSession} from '../../../shared/services/dbUtils';
import {songPathFromUrl} from '../../../shared/services/songPathUtils';
import {searchIndexUtils} from '../services/song-search-index-utils';
import {buildSongLibrary, buildSongLibraryCatalog} from '../services/song-library-model';
import {libraryDocument} from '../services/song-library-documents';
import {FAVORITES_MAP, USAGE_MAP, LIBRARY_LOCAL_EVENT, readSongLibraryState, recordSongUse, setSongFavorite} from '../services/song-library-store';

const emptyState = {version: 1, favorites: [], usage: []};
const SongLibraryContext = createContext({entries: [], loading: false, error: '', toggleFavorite: () => {}});
export const useSongLibrary = () => useContext(SongLibraryContext);

export const SongLibraryProvider = ({children}) => {
  const {currentSessionId} = useContext(AppCtx);
  const workspaceId = getWorkspaceIdForSession(currentSessionId);
  const location = useLocation();
  const [snapshot, setSnapshot] = useState({workspaceId: '', documents: [], state: emptyState, loading: true, error: ''});
  const lastRoute = useRef('');
  useEffect(() => {
    const {doc} = yjsDocumentService.connectToDocument(workspaceId);
    let active = true, timer, revision = 0;
    const cache = new Map();
    const relevantTypes = new Set([doc.getMap('files'), doc.getMap(FAVORITES_MAP), doc.getMap(USAGE_MAP)]);
    const refresh = async () => {
      const version = ++revision;
      try {
        const paths = [...doc.getMap('files').keys()].filter(path => /\.txt$/i.test(path)).sort();
        const documents = [];
        for (const path of paths) {
          const text = doc.getText('content:' + path);
          relevantTypes.add(text);
          const content = text.toString();
          let entry = cache.get(path);
          if (!entry || entry.content !== content) {
            entry = {content, documents: (await searchIndexUtils.explodeSongs(path, content)).map(libraryDocument)};
            cache.set(path, entry);
          }
          documents.push(...entry.documents);
        }
        [...cache.keys()].forEach(path => { if (!paths.includes(path)) cache.delete(path); });
        if (active && version === revision) {
          const state = readSongLibraryState(workspaceId);
          setSnapshot(previous => {
            const sameDocuments = previous.workspaceId === workspaceId && previous.documents.length === documents.length &&
              previous.documents.every((item, index) => item === documents[index]);
            if (sameDocuments && !previous.loading && !previous.error && JSON.stringify(previous.state) === JSON.stringify(state)) return previous;
            return {workspaceId, documents: sameDocuments ? previous.documents : documents, state, loading: false, error: ''};
          });
        }
      } catch (error) {
        if (active && version === revision) setSnapshot(previous => ({...previous, ...(previous.workspaceId !== workspaceId ? {documents: [], state: emptyState} : {}), workspaceId, loading: false, error: error.message}));
      }
    };
    const schedule = () => { ++revision; clearTimeout(timer); timer = setTimeout(refresh, 100); };
    const onTransaction = transaction => {
      if ([...transaction.changedParentTypes.keys()].some(type => relevantTypes.has(type))) schedule();
    };
    doc.on('afterTransaction', onTransaction);
    window.addEventListener(LIBRARY_LOCAL_EVENT, schedule);
    window.addEventListener('storage', schedule);
    refresh();
    return () => {
      active = false; clearTimeout(timer);
      doc.off('afterTransaction', onTransaction);
      window.removeEventListener(LIBRARY_LOCAL_EVENT, schedule);
      window.removeEventListener('storage', schedule);
    };
  }, [workspaceId]);

  const current = snapshot.workspaceId === workspaceId;
  const catalog = useMemo(() => current ? buildSongLibraryCatalog(snapshot.documents) : [], [current, snapshot.documents]);
  const entries = useMemo(() => current ? buildSongLibrary(snapshot.documents, snapshot.state, catalog) : [],
    [current, snapshot.documents, snapshot.state, catalog]);
  const variantIndex = useMemo(() => {
    const index = new Map();
    for (const entry of entries) for (const variant of entry.variants) {
      index.set(JSON.stringify([variant.fileName, variant.songNumber]), entry);
    }
    return index;
  }, [entries]);
  const findVariant = useCallback((fileName, songNumber) => variantIndex.get(JSON.stringify([fileName, songNumber])), [variantIndex]);
  const route = `${workspaceId}:${location.pathname}${location.hash || ''}`;
  useEffect(() => {
    if (!current || snapshot.loading || snapshot.error) return;
    if (!/^\/songs\/.*\.txt$/i.test(location.pathname)) { lastRoute.current = route; return; }
    if (lastRoute.current === route) return;
    const fileName = songPathFromUrl(location.pathname);
    const match = (location.hash || '').match(/^#song-title-(\d+)$/);
    const songNumber = match ? Number(match[1]) : 1;
    const document = snapshot.documents.find(item => item.fileName === fileName && item.songNumber === songNumber);
    if (!document) return;
    try { recordSongUse(workspaceId, document); lastRoute.current = route; }
    catch (error) { setSnapshot(previous => ({...previous, error: error.message})); }
  }, [current, snapshot, route, workspaceId, location.pathname, location.hash]);

  const toggleFavorite = useCallback(entry => {
    try {
      const favorite = readSongLibraryState(workspaceId).favorites.some(record => entry.keys.includes(record.songKey));
      setSongFavorite(workspaceId, entry, !favorite);
    }
    catch (error) { setSnapshot(previous => ({...previous, error: error.message})); }
  }, [workspaceId]);
  const value = useMemo(() => ({entries, findVariant, loading: !current || snapshot.loading,
    error: current ? snapshot.error : '', toggleFavorite}), [entries, findVariant, current, snapshot.loading, snapshot.error, toggleFavorite]);
  return <SongLibraryContext.Provider value={value}>{children}</SongLibraryContext.Provider>;
};
